import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

// Stable key for matching a combination across regenerations, independent of the
// order option names were typed in (e.g. {Size:'2.6',Color:'Gold'} and
// {Color:'Gold',Size:'2.6'} must collide on the same row).
const variantKey = (optionValues) =>
  Object.keys(optionValues)
    .sort()
    .map((k) => `${k}:${optionValues[k]}`)
    .join('|');

// Collapses option drafts that share a name (case-insensitively) into one, combining
// their values instead of letting the later one silently overwrite the earlier one.
// Two rows both named "Color" - one used to add a second option TYPE when the user
// meant to add a second VALUE to the existing one - is exactly the bug this guards
// against: without merging, generateCombinations/handleSave key their internal lookups
// by name, so the second "Color" row would clobber the first's values entirely.
const mergeDuplicateOptionNames = (drafts) => {
  const merged = [];
  const indexByKey = new Map();
  drafts.forEach((draft) => {
    const name = draft.name.trim();
    const key = name.toLowerCase();
    if (!key) {
      merged.push(draft);
      return;
    }
    if (indexByKey.has(key)) {
      const idx = indexByKey.get(key);
      const existingValues = merged[idx].valuesText.split(',').map((v) => v.trim()).filter(Boolean);
      const newValues = draft.valuesText.split(',').map((v) => v.trim()).filter(Boolean);
      merged[idx] = {
        ...merged[idx],
        valuesText: Array.from(new Set([...existingValues, ...newValues])).join(', ')
      };
    } else {
      indexByKey.set(key, merged.length);
      merged.push({ ...draft, name });
    }
  });
  return merged;
};

const cartesianProduct = (arraysByName) => {
  const names = Object.keys(arraysByName);
  if (names.length === 0) return [];
  return names.reduce(
    (acc, name) => {
      const values = arraysByName[name];
      const next = [];
      acc.forEach((combo) => {
        values.forEach((value) => {
          next.push({ ...combo, [name]: value });
        });
      });
      return next;
    },
    [{}]
  );
};

/**
 * Modal for defining a product's options (Size, Color, Weight, Chain Length, ...) and
 * the purchasable variant combinations (each with its own stock, and optionally its
 * own price/SKU/image). "Free Size" is just another value typed into the Size option -
 * no special handling needed for it.
 *
 * Props:
 *  - productId, basePrice: the product this belongs to
 *  - onClose(): dismiss the modal
 */
export default function ProductVariantsManager({ productId, basePrice, getToken, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [optionDrafts, setOptionDrafts] = useState([{ name: '', valuesText: '' }]);
  const [variantRows, setVariantRows] = useState([]); // [{key, option_values, stock, price_override, sku, image_urls, is_active}]
  const [uploadingRowKey, setUploadingRowKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/products/${productId}/variants`);
      const json = await res.json();
      if (json.success) {
        const { options, variants } = json.data;

        // Reconstruct each option's value list from what actually appears across the
        // saved variants (product_options only stores the option NAMES).
        const valuesByOption = {};
        options.forEach((o) => { valuesByOption[o.name] = new Set(); });
        variants.forEach((v) => {
          Object.entries(v.option_values || {}).forEach(([name, value]) => {
            if (!valuesByOption[name]) valuesByOption[name] = new Set();
            valuesByOption[name].add(value);
          });
        });

        setOptionDrafts(
          options.length > 0
            ? mergeDuplicateOptionNames(
                options.map((o) => ({ name: o.name, valuesText: Array.from(valuesByOption[o.name] || []).join(', ') }))
              )
            : [{ name: '', valuesText: '' }]
        );
        setVariantRows(
          variants.map((v) => ({
            key: variantKey(v.option_values),
            option_values: v.option_values,
            stock: v.stock,
            price_override: v.price_override ?? '',
            sku: v.sku || '',
            image_urls: v.image_urls || [],
            is_active: v.is_active !== 0
          }))
        );
      }
    } catch (error) {
      console.error('Load product variants error:', error);
      toast.error('Failed to load variants');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  const updateOptionDraft = (index, patch) => {
    setOptionDrafts((prev) => {
      const next = prev.map((o, i) => (i === index ? { ...o, ...patch } : o));
      // Only re-merge when the name field changed - merging on every keystroke of the
      // *values* field would be harmless but pointless work.
      return patch.name !== undefined ? mergeDuplicateOptionNames(next) : next;
    });
  };

  const addOptionType = () => {
    setOptionDrafts((prev) => [...prev, { name: '', valuesText: '' }]);
  };

  const removeOptionType = (index) => {
    setOptionDrafts((prev) => prev.filter((_, i) => i !== index));
  };

  // Regenerates the combination grid from the current option drafts, keeping
  // stock/price/sku for any combination that already existed (matched by variantKey)
  // so editing one option's values doesn't wipe data already entered for the others.
  const generateCombinations = () => {
    const arraysByName = {};
    mergeDuplicateOptionNames(optionDrafts).forEach((o) => {
      const name = o.name.trim();
      if (!name) return;
      const values = o.valuesText.split(',').map((v) => v.trim()).filter(Boolean);
      if (values.length > 0) arraysByName[name] = values;
    });

    if (Object.keys(arraysByName).length === 0) {
      toast.error('Add at least one option with values first (e.g. Size: 2.4, 2.6, 2.8)');
      return;
    }

    const existingByKey = new Map(variantRows.map((r) => [r.key, r]));
    const combos = cartesianProduct(arraysByName);
    setVariantRows(
      combos.map((option_values) => {
        const key = variantKey(option_values);
        const existing = existingByKey.get(key);
        return existing
          ? { ...existing, option_values }
          : { key, option_values, stock: 0, price_override: '', sku: '', image_urls: [], is_active: true };
      })
    );
  };

  const updateRow = (key, patch) => {
    setVariantRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const removeRow = (key) => {
    setVariantRows((prev) => prev.filter((r) => r.key !== key));
  };

  // Per-variant photos matter most for a "Color" option - the first one is what
  // listing-page swatches swap to when clicked (ProductMediaWithSwatches), and the
  // full set replaces the main gallery on the product page once that color is picked
  // (ProductDetail). Optional for every other option type; falls back to the
  // product's own main image/gallery when left blank. Multiple files can be added at
  // once, and more can be appended later - nothing here overwrites what's already set.
  const handleRowImageUpload = async (key, files) => {
    const fileList = Array.from(files || []);
    if (fileList.length === 0) return;
    setUploadingRowKey(key);
    try {
      const token = getToken(true);
      const uploaded = [];
      for (const file of fileList) {
        const formData = new FormData();
        formData.append('image', file);
        const res = await fetch('/api/upload/image', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
        const json = await res.json();
        if (json.success && json.data?.image_url) {
          uploaded.push(json.data.image_url);
        } else {
          toast.error(json.message || 'Failed to upload an image');
        }
      }
      if (uploaded.length > 0) {
        setVariantRows((prev) =>
          prev.map((r) => (r.key === key ? { ...r, image_urls: [...(r.image_urls || []), ...uploaded] } : r))
        );
      }
    } catch (error) {
      console.error('Variant image upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingRowKey(null);
    }
  };

  const removeRowImage = (key, urlToRemove) => {
    setVariantRows((prev) =>
      prev.map((r) => (r.key === key ? { ...r, image_urls: (r.image_urls || []).filter((u) => u !== urlToRemove) } : r))
    );
  };

  const handleSave = async () => {
    const options = mergeDuplicateOptionNames(optionDrafts)
      .map((o) => o.name.trim())
      .filter(Boolean)
      .map((name, i) => ({ name, display_order: i }));

    if (options.length > 0 && variantRows.length === 0) {
      toast.error('Click "Generate Combinations" before saving, or clear the option fields to remove variants entirely');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`/api/products/${productId}/variants`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken(true)}`
        },
        body: JSON.stringify({
          options,
          variants: variantRows.map((r) => ({
            option_values: r.option_values,
            stock: Number(r.stock) || 0,
            price_override: r.price_override === '' ? null : Number(r.price_override),
            sku: r.sku || null,
            image_urls: r.image_urls || [],
            is_active: r.is_active
          }))
        })
      });
      const json = await res.json();
      if (json.success) {
        toast.success('Variants saved');
        onClose();
      } else {
        toast.error(json.message || 'Failed to save variants');
      }
    } catch (error) {
      console.error('Save product variants error:', error);
      toast.error('Failed to save variants');
    } finally {
      setSaving(false);
    }
  };

  const optionNamesInUse = mergeDuplicateOptionNames(optionDrafts).map((o) => o.name.trim()).filter(Boolean);

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Product Options & Variants</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Define option types (Size, Color, Weight, Chain Length...) and their values. For a one-size item, just enter a single value like "Free Size".
              For a two-tone Color value, use a "+" like "Ruby+Green" - it renders as a split-color dot. If a color already exists, add its new value to that
              same row instead of adding another "Color" row - two rows with the same name will now auto-merge, but it's cleaner to just edit the one row.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Option type editor */}
            <div className="space-y-3">
              {optionDrafts.map((o, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <input
                    type="text"
                    placeholder="Option name (e.g. Size)"
                    value={o.name}
                    onChange={(e) => updateOptionDraft(i, { name: e.target.value })}
                    className="w-40 px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <input
                    type="text"
                    placeholder="Values, comma-separated (e.g. 2.4, 2.6, 2.8, Free Size)"
                    value={o.valuesText}
                    onChange={(e) => updateOptionDraft(i, { valuesText: e.target.value })}
                    className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={() => removeOptionType(i)}
                    className="p-2 text-gray-400 hover:text-red-600"
                    title="Remove this option type"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              ))}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={addOptionType}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                >
                  <PlusIcon className="w-4 h-4" /> Add option type
                </button>
                <button
                  type="button"
                  onClick={generateCombinations}
                  className="px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 text-sm font-semibold hover:bg-indigo-100"
                >
                  Generate Combinations
                </button>
              </div>
            </div>

            {/* Combination grid */}
            {variantRows.length > 0 && (
              <div className="border-2 border-gray-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        {optionNamesInUse.map((name) => (
                          <th key={name} className="px-3 py-2 text-left font-semibold text-gray-600">{name}</th>
                        ))}
                        <th className="px-3 py-2 text-left font-semibold text-gray-600">Photos</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-600">Stock</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-600">
                          Price override <span className="font-normal text-gray-400">(blank = ₹{basePrice})</span>
                        </th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-600">SKU</th>
                        <th className="px-3 py-2 text-center font-semibold text-gray-600">Active</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {variantRows.map((row) => (
                        <tr key={row.key} className={row.is_active ? '' : 'opacity-50'}>
                          {optionNamesInUse.map((name) => (
                            <td key={name} className="px-3 py-2 font-medium text-gray-700">
                              {row.option_values[name] ?? '—'}
                            </td>
                          ))}
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1.5 flex-wrap max-w-[180px]">
                              {(row.image_urls || []).map((url) => (
                                <div key={url} className="relative group/thumb">
                                  <img src={url} alt="" className="w-9 h-9 rounded object-cover border border-gray-200" />
                                  <button
                                    type="button"
                                    onClick={() => removeRowImage(row.key, url)}
                                    className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-600 text-white text-[10px] leading-4 opacity-0 group-hover/thumb:opacity-100"
                                    title="Remove this photo"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                              <label className="cursor-pointer group">
                                <span className="w-9 h-9 rounded border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 text-xs group-hover:border-indigo-400">
                                  {uploadingRowKey === row.key ? '...' : '+'}
                                </span>
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  className="hidden"
                                  disabled={uploadingRowKey === row.key}
                                  onChange={(e) => handleRowImageUpload(row.key, e.target.files)}
                                />
                              </label>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              min="0"
                              value={row.stock}
                              onChange={(e) => updateRow(row.key, { stock: e.target.value })}
                              className="w-20 px-2 py-1 border border-gray-300 rounded"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="—"
                              value={row.price_override}
                              onChange={(e) => updateRow(row.key, { price_override: e.target.value })}
                              className="w-24 px-2 py-1 border border-gray-300 rounded"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={row.sku}
                              onChange={(e) => updateRow(row.key, { sku: e.target.value })}
                              className="w-28 px-2 py-1 border border-gray-300 rounded"
                            />
                          </td>
                          <td className="px-3 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={row.is_active}
                              onChange={(e) => updateRow(row.key, { is_active: e.target.checked })}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <button type="button" onClick={() => removeRow(row.key)} className="text-gray-400 hover:text-red-600">
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0 bg-white">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-xl border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="px-4 py-2 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Variants'}
          </button>
        </div>
      </div>
    </div>
  );
}
